import { type HttpClient, type MultipartBody, query, segment } from '../http';

import {
  type CurrentUser,
  type SearchUsersResult,
  type UpdateProfilePayload,
  type UserProfile,
} from './users.types';

/**
 * Profile, avatar, account search and account deletion.
 *
 * @param http Transport supplied by the application.
 * @returns The `users` domain of the API client.
 */
export function createUsersApi(http: HttpClient) {
  return {
    /**
     * Everything `/u/:username` draws, for whoever is reading it.
     *
     * Warning: addressed by **username**, because that is what the address
     * bar holds — a profile link is meant to be readable and shareable.
     *
     * @param username Whose profile.
     * @returns The profile, with the owner's own figures when it is theirs.
     * @throws 404 when there is no such account, or they blocked the reader —
     *   deliberately the same answer.
     */
    /**
     * Says the first-launch sequence has been seen.
     *
     * Warning: stamped on the **account**, not the device — a reinstall or a
     * second phone is not a first launch. Idempotent.
     *
     * @returns The profile, its `onboardedAt` now set.
     */
    markOnboarded: () => http.post<CurrentUser>('/users/me/onboarded', {}),

    profile: (username: string) =>
      http.get<UserProfile>(`/users/by-username/${segment(username)}/profile`),

    /**
     * Reads the signed-in account, entitlements included.
     *
     * @returns The profile, whose `features` lists the active paid features.
     */
    getMe: () => http.get<CurrentUser>('/users/me'),

    /**
     * Updates the editable part of the profile.
     *
     * @param payload Display name and avatar URL — the only fields the backend accepts.
     * @returns The updated profile.
     */
    updateMe: (payload: UpdateProfilePayload) =>
      http.patch<CurrentUser>('/users/me', payload),

    /**
     * Replaces the profile picture with a file from the device.
     *
     * The server re-encodes the image, updates `avatarUrl` and answers the
     * updated profile, so no `PATCH` has to follow.
     *
     * @param body Multipart body carrying the file under the `file` key.
     * @returns The updated profile.
     */
    uploadAvatar: (body: MultipartBody) =>
      http.post<CurrentUser>('/users/me/avatar', body),

    /**
     * Removes the profile picture; succeeds even when there was none.
     *
     * @returns The updated profile.
     */
    removeAvatar: () => http.delete<CurrentUser>('/users/me/avatar'),

    /**
     * Searches accounts by username or display name, closest match first.
     *
     * Answers a ranked shortlist — never the whole match set — plus `total`, so
     * the screen can invite a narrower term instead of offering a next page.
     * Excludes the caller, deleted accounts and anyone who blocked them.
     *
     * @param q Free-text term, matched case-insensitively.
     * @param limit Shortlist size; the server defaults to 10 and caps at 25.
     * @returns The ranked matches and how many accounts matched in total.
     */
    search: (q: string, limit?: number) =>
      http.get<SearchUsersResult>(`/users/search${query({ q, limit })}`),

    /**
     * Deletes the signed-in account.
     *
     * Warning: Soft delete server-side, and every session is revoked with it: the
     * client must treat its session as lost as soon as this resolves.
     */
    deleteMe: () => http.delete<void>('/users/me'),
  };
}

export type UsersApi = ReturnType<typeof createUsersApi>;
